import React from 'react';
import { FormattedMessage } from 'react-intl';
import { getErrorMessage } from '../../utils/errorMessage';
import { setPassword } from './setPassword.api';
import { doctrineAnalytics } from '../../utils/analyticsUtils';
import { getParametersFromUrl } from '../../utils/url-utils/urlUtils';
import {
  ModalConfirmationContainer,
  ModalConfirmationHeader,
  ModalConfirmationFooter,
  ModalConfirmationContent,
  ModalActions,
} from '../../components/modal';
import { BrandButton } from '../../components/button';
import styles from './set_password_modal.module.scss';

type Props = {
  closeModal: () => void;
  isOpen: boolean;
  passwordResetData: {
    email: string;
    passwordResetCode: string;
    userId: number;
  };
};

type State = {
  password: string;
  error: string;
  loading: boolean;
  seePassword: boolean;
};

export class SetPasswordModal extends React.Component<Props, State> {
  static defaultProps = {
    closeModal: () => {},
    isOpen: false,
  };

  state: State = {
    password: '',
    error: '',
    loading: false,
    seePassword: false,
  };

  closeAndReload = () => {
    this.props.closeModal();
    window.location.href = '/?source=signup-password-modal';
  };

  submitSetPassword = async (event: React.FormEvent<HTMLFormElement>) => {
    const { passwordResetCode, userId } = this.props.passwordResetData;
    const { password } = this.state;
    event.preventDefault();
    try {
      this.setState({ loading: true });
      await setPassword({ passwordResetCode, userId, password });
      window.location.href = '/?source=signup-password-modal';
      doctrineAnalytics('Account PasswordChanged', { user_id: userId });
    } catch (error) {
      if (error.response) {
        this.setState({
          error: getErrorMessage(error.response),
          loading: false,
        });
      }
    }
  };

  handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ password: event.target.value, loading: false });
  };

  toggleSeeClearPassword = () => {
    doctrineAnalytics('Account SeePasswordToggle', { source: 'set-password' });
    this.setState(({ seePassword }) => ({
      seePassword: !seePassword,
    }));
  };

  render() {
    const { passwordResetData, isOpen, closeModal } = this.props;
    const { workflow } = getParametersFromUrl(window.location.search);
    return (
      <ModalConfirmationContainer
        isOpen={isOpen}
        onRequestClose={closeModal}
        shouldCloseOnOverlayClick={false}
      >
        <ModalConfirmationHeader
          title={
            <FormattedMessage id="account/set-password-modal/SetPasswordModal.tsx/1" />
          }
          onClose={this.closeAndReload}
        />

        <form onSubmit={this.submitSetPassword}>
          <ModalConfirmationContent>
            {workflow === 'invite_finish' && (
              <div className={styles.row}>
                {' '}
                <FormattedMessage id="account/set-password-modal/SetPasswordModal.tsx/9" />{' '}
              </div>
            )}
            <div className={styles.row}>
              <FormattedMessage
                id="account/set-password-modal/SetPasswordModal.tsx/2"
                values={{ email: passwordResetData.email }}
              />
            </div>
            <div className={styles.row}>
              <label htmlFor="input-password" className={styles.label}>
                <div className={styles.labelGroup}>
                  <div>
                    <FormattedMessage id="account/set-password-modal/SetPasswordModal.tsx/3" />{' '}
                    <span className={styles.labelDiscreet}>
                      <FormattedMessage id="account/set-password-modal/SetPasswordModal.tsx/4" />
                    </span>
                  </div>
                  <button
                    type="button"
                    className={styles.seeButton}
                    onClick={this.toggleSeeClearPassword}
                  >
                    {this.state.seePassword ? (
                      <FormattedMessage id="account/set-password-modal/SetPasswordModal.tsx/6" />
                    ) : (
                      <FormattedMessage id="account/set-password-modal/SetPasswordModal.tsx/5" />
                    )}
                  </button>
                </div>
                <input
                  required
                  id="input-password"
                  type={this.state.seePassword ? 'text' : 'password'}
                  value={this.state.password}
                  onChange={this.handleChange}
                  spellCheck={false}
                  autoComplete="new-password"
                />
              </label>
            </div>
            {this.state.error && (
              <div className={styles.error}>{this.state.error}</div>
            )}
          </ModalConfirmationContent>
          <ModalConfirmationFooter>
            <ModalActions>
              <BrandButton type="submit" disabled={this.state.loading}>
                {this.state.loading ? (
                  <FormattedMessage id="account/set-password-modal/SetPasswordModal.tsx/7" />
                ) : (
                  <FormattedMessage id="account/set-password-modal/SetPasswordModal.tsx/8" />
                )}
              </BrandButton>
            </ModalActions>
          </ModalConfirmationFooter>
        </form>
      </ModalConfirmationContainer>
    );
  }
}
